'use client';
import { useState } from 'react';
import { Collapse, IconButton } from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

export const columns = [
  [
    { name: 'Welcome Series', slug: 'welcome', shortDescription: 'Discover the best SMS welcome flows from brands like Graza, Bobbie, Lemme and even the NYC mayor. Browse screenshots and breakdowns to learn what works in high-converting onboarding sequences.' },
    { name: 'Cart Abandon', slug: 'cart', shortDescription: 'See how top DTC brands recover lost sales with cart abandonment SMS messages. Get ideas for timing, copywriting, and urgency tactics that actually drive conversions.' },
    { name: 'Reviews', slug: 'reviews', shortDescription: 'See how brands use SMS to highlight real customer reviews and build instant credibility. These texts turn social proof into a sales driver, showcasing happy buyers and five-star feedback.' },
    { name: 'Back-In-Stock', slug: 'back in stock', shortDescription: 'Browse SMS alerts that drive urgency and sell out fast when products return to stock. Learn how brands keep subscribers engaged with real-time restock notifications.' },
    { name: 'Price Drop', slug: 'price drop', shortDescription: 'View SMS messages that alert customers to discounts and sudden price drops. These examples show how urgency, timing, and clear copy turn markdowns into sales.' },
  ],
  [
    { name: 'Birthday', slug: 'your birthday', shortDescription: 'Celebrate with real birthday SMS campaigns from a variety of brands. Get inspired by fun, personalized texts that surprise and delight customers on their special day.' },
    { name: 'New Year\'s', slug: 'happy new year', shortDescription: 'Explore SMS messages that kick off the New Year with energy and intent. From resolutions to first-of-year promos, these texts are all about fresh starts.' },
    { name: 'Valentine\'s Day', slug: 'valentine', shortDescription: 'See how brands use Valentine\'s Day SMS campaigns to drive gifting and romance-themed sales. From cheeky copy to last-minute deals, love is in the text.' },
    { name: 'Mother\'s Day', slug: 'mother', shortDescription: 'Browse SMS examples that help shoppers celebrate moms and maternal figures. These texts tap into emotion, urgency, and thoughtful product positioning.' },
    { name: 'Summer Holidays', slug: 'july 4th', shortDescription: 'From Memorial Day to Labor Day, explore summer holiday SMS campaigns that heat up seasonal sales. Find examples packed with energy, sunshine, and warm-weather promos.' },
    { name: 'Halloween', slug: 'halloween', shortDescription: 'Browse SMS campaigns that embrace spooky season with creativity and flair. From costumes to candy-colored promos, these texts bring the Halloween spirit to life.' },
    { name: 'Black Friday', slug: 'Cyber', shortDescription: 'Explore high-performing SMS messages from Black Friday and Cyber Monday campaigns. These examples show how brands use urgency, exclusivity, and time-sensitive deals to win the biggest shopping weekend of the year.' },
    { name: 'Christmas', slug: 'christmas', shortDescription: 'Explore festive SMS messages sent during the holiday rush. These texts help brands drive last-minute sales, share shipping cutoffs, and keep the cheer flowing.' },
  ],
  [
    { name: 'Funniest Texts', slug: 'funniest', shortDescription: 'Laugh your way through SMS messages that break the mold with humor. These texts show how brands stand out with personality and wit.' },
    { name: 'Most Creative Texts', slug: 'creative', shortDescription: 'Get inspired by SMS campaigns that go beyond the basics. These texts use clever angles, unexpected formats, and bold ideas to capture attention.' },
    { name: 'Most Educational Texts', slug: 'educational', shortDescription: 'Explore SMS examples that teach, explain, or guide customers with real value. From health and wellness to civic lessons, these messages build trust and drive engagement.' },
  ],
];

export default function InspirationMenu({ isMobile = false, onItemClick = null, setIsLoading }) {
  const [isExpanded, setIsExpanded] = useState(false);

  const handleClick = (query) => {
    setIsLoading(true);
    const encoded = encodeURIComponent(query)?.replace(/\+/g, '%20');
    window.location.href = `/search?query=${encoded}&_rsc=acgkz`;
    
    if (isMobile && onItemClick) {
      onItemClick();
    }
  };

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
  };

  if (!isMobile) {
    return (
      <div
        className="relative"
        onMouseEnter={() => setIsExpanded(true)}
        onMouseLeave={() => setIsExpanded(false)}
      >
        <button className="py-2 text-base font-medium text-black md:py-4 md:text-xl md:font-normal">
          Inspiration
        </button>
        {isExpanded && (
          <>
            {/* Invisible bridge to maintain hover state */}
            <div
              style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                height: '8px',
                zIndex: 49,
              }}
            />
            
            {/* Actual dropdown */}
            <div
              style={{
                position: 'absolute',
                backgroundColor: '#00BFFF',
                zIndex: 50,
                color: '#000',
                left: window.innerWidth >= 1024 ? '-30.2rem' : '-3rem',
                top: '100%',
                marginTop: '8px',
                width: window.innerWidth >= 1024 ? 'calc(100vw + 30.2rem)' : 'calc(100vw + 3rem)',
              }}
            >
              <div 
                className="flex justify-center gap-2 px-4 py-6 mx-auto md:px-12 md:gap-40 md:justify-start" 
                style={{ flexDirection: window.innerWidth >= 1024 ? 'row' : 'column' }}
              >
                {columns.map((col, colIdx) => (
                  <div key={colIdx} className="flex flex-col gap-2">
                    {col.map((item) => (
                      <button
                        key={item.slug}
                        onClick={() => handleClick(item.slug)}
                        className="block text-sm font-normal text-left text-black transition-colors duration-200 md:text-xl hover:underline whitespace-nowrap"
                      >
                        {item.name}
                      </button>
                    ))}
                  </div>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    );
  }

  // Mobile version (collapsible)
  return (
    <div className='w-full'>
      <IconButton
        onClick={handleToggle}
        sx={{
          display: 'flex',
          borderRadius: 0,
          width: '100%',
          position: 'relative',
          justifyContent: 'center',
          alignItems: 'center',
          px: 0,
          py: 2,
          '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
        }}
        aria-label='Toggle inspiration menu'
      >
        <span className='text-base font-medium text-gray-800'>
          Inspiration
        </span>
        
        <div style={{ position: 'absolute', right: '12px' }}>
          {isExpanded ?
            <ExpandLessIcon sx={{ color: 'gray', fontSize: 20 }} /> :
            <ExpandMoreIcon sx={{ color: 'gray', fontSize: 20 }} />
          }
        </div>
      </IconButton>
      
      <Collapse in={isExpanded} timeout="auto" unmountOnExit>
        <div className='px-4 py-2 bg-[#00BFFF]'>
          <div className="space-y-4">
            {columns.map((col, colIdx) => (
              <div key={colIdx} className="space-y-2">
                <div className="flex flex-col gap-1 items-center">
                  {col.map((item) => (
                    <button
                      key={item.slug}
                      onClick={() => handleClick(item.slug)}
                      className="block px-3 py-2 text-sm font-medium text-left transition-colors duration-200 rounded-md hover:bg-blue-50 hover:text-blue-700 active:bg-blue-100 hover:underline"
                      title={item.shortDescription}
                    >
                      {item.name}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </Collapse>
    </div>
  );
}